import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import config from 'config'
import * as Sentry from '@sentry/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { captureException } from '@sentry/react'
import {
  AdyenCheckout,
  Card,
  PayPal,
  Swish,
  Klarna,
  Redirect,
  ApplePay,
  OnlineBankingPL,
  Bancontact
} from '@adyen/adyen-web'
import Parse from 'parse'
import { getCurrentParseUser } from '../../../common/user/currentUser'
import { ADYEN_PAYMENT_OPTIONS, getRegion } from '../../../common/region/region'
import { reportAnalyticsCheckoutV2Purchase } from '../../../common/cart/actions'
import { nothingToPayWithAdyen } from '../../components/sessionPayment/components/utils'
import { ERRORS } from '../../components/sessionPayment/components/ErrorHandler'

const clearUrl = ({ urlParams, navigate }) => {
  // Remove redirectResult/sessionId-params to make checkout interactive again
  urlParams.delete('redirectResult')
  urlParams.delete('sessionId')
  navigate(
    {
      search: urlParams.toString()
    },
    { replace: true }
  )
}

const isApplePayAvailable = async () => {
  if (window.ApplePaySession) return window.ApplePaySession?.canMakePayments()
  return false
}

const getDetailedType = (component) =>
  component.props?.storedPaymentMethodId ? 'saved_card' : component.type

const useAdyenPaymentMethods = () => {
  const cardRef = useRef()
  const savedCardRef = useRef()
  const klarnaRef = useRef()
  const klarnaPaynowRef = useRef()
  const klarnaAccountRef = useRef()
  const swishRef = useRef()
  const paypalRef = useRef()
  const idealRef = useRef()
  const mobilepayRef = useRef()
  const bancontactRef = useRef()
  const onlineBankingPLRef = useRef()
  const applepayRef = useRef()

  const adyenPaymentMethodRefs = useMemo(
    () => ({
      card: cardRef,
      saved_card: savedCardRef,
      klarna: klarnaRef,
      klarna_paynow: klarnaPaynowRef,
      klarna_account: klarnaAccountRef,
      swish: swishRef,
      paypal: paypalRef,
      ideal: idealRef,
      mobilepay: mobilepayRef,
      bcmc: bancontactRef,
      onlineBanking_PL: onlineBankingPLRef,
      applepay: applepayRef
    }),
    [cardRef, savedCardRef, klarnaRef, klarnaPaynowRef, swishRef, paypalRef, idealRef, applepayRef]
  )
  return { adyenPaymentMethodRefs }
}

const useAdyenCheckout = ({
  createSessionCallback,
  commenceSessionCallback,
  adyenCommenceSessionAnalyticsCallback,
  isLoyaltyCheckout = false,
  isAppCheckout = false,
  totalPrice,
  creditAmount,
  getDataUsageConsent
}) => {
  const [adyenCheckout, setAdyenCheckout] = useState()
  const [paymentId, setPaymentId] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const navigate = useNavigate()
  const userId = getCurrentParseUser()?.id
  const { search, pathname } = useLocation()

  const commencePayment = useCallback(
    async ({ dataUsageConsent, paymentOptions }) => {
      setLoading(true)
      try {
        if (nothingToPayWithAdyen(adyenCheckout)) {
          reportAnalyticsCheckoutV2Purchase(paymentId)
          const commenceSessionParams = { paymentId, creditAmount, dataUsageConsent }
          adyenCommenceSessionAnalyticsCallback?.(commenceSessionParams)
          await commenceSessionCallback(commenceSessionParams).then((payment) => {
            navigate(`/payment/${payment.id}`)
          })
        } else {
          const paymentComponent = adyenCheckout?.components?.find(
            ({ _id }) => _id === paymentOptions
          )

          if (!paymentComponent) throw new Error('No payment component found')
          paymentComponent.submit()

          if (paymentComponent.state && !paymentComponent.state.isValid) {
            setLoading(false)
          }
        }
      } catch (error) {
        captureException(error)
        if (error instanceof Parse.Error) {
          setError({ type: ERRORS.PARSE_ERROR, details: error })
        } else {
          setError({ type: ERRORS.ADYEN_ERROR, details: { message: error.message } })
        }
        setLoading(false)
      }
    },
    [adyenCheckout, paymentId, creditAmount, commenceSessionCallback, navigate, setError]
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(search)
    const urlSessionId = urlParams.get('sessionId')
    const redirectResult = urlParams.get('redirectResult')
    const getSession = async () => {
      setLoading(true)
      let session
      try {
        session = urlSessionId
          ? { id: urlSessionId }
          : await createSessionCallback({
              creditAmount: parseFloat(creditAmount),
              returnUrl: `${window.location.origin}${pathname}`
            })
      } catch (e) {
        captureException(e)
        setError({ type: ERRORS.PARSE_ERROR, details: e })
        setLoading(false)
        setAdyenCheckout(null)
        return
      }
      setPaymentId(session.reference)
      /**
       * If adyen amount is zero the result of the createSessionWrapper is an object
       * that only includes reference.
       */
      if (!session.id) {
        setAdyenCheckout(null)
        setLoading(false)
        return
      }
      try {
        const checkout = await AdyenCheckout({
          countryCode: session.countryCode || getRegion(),
          locale: session.shopperLocale,
          environment: config.adyen.environment,
          clientKey: config.adyen.clientKey,
          analytics: {
            enabled: false
          },
          session,
          showPayButton: false,
          onPaymentFailed: async (result) => {
            clearUrl({ urlParams, navigate })
            switch (result.resultCode) {
              case 'Cancelled':
                setLoading(false)
                await Parse.Cloud.run('cancelPaymentBySessionId', {
                  adyenSessionId: session.id
                })
                break
              case 'Error':
                captureException(result)

                setError({
                  type: ERRORS.ADYEN_ERROR,
                  details: {
                    message: result.resultCode
                  }
                })
                setLoading(false)
                break
              case 'Refused':
                setError({
                  type: ERRORS.ADYEN_ERROR,
                  details: {
                    message: result.resultCode
                  }
                })
                setLoading(false)
                break
            }
          },

          onPaymentCompleted: async (result) => {
            if (result.resultCode === 'Authorised') {
              // 2024-09: Setting query param 'authorised' is only kept for old app clients.
              // It can be removed when clients have been updated.
              navigate(`/advance-payment/${session.id}?authorised=true`)
            } else {
              Sentry.captureException(
                new Error(`resultCode is not 'Authorised', instead it's ${result.resultCode}.`)
              )
              clearUrl({ urlParams, navigate })
            }
          },

          onError: (error) => {
            clearUrl({ urlParams, navigate })
            captureException(error)

            setError({ type: ERRORS.ADYEN_ERROR, details: { message: error.message } })
          },

          beforeSubmit: async (data, component, actions) => {
            try {
              const dataUsageConsent = await getDataUsageConsent()
              const commenceSessionParams = {
                paymentId: session.reference,
                adyenPaymentData: { adyenSessionId: session.id, ...data },
                creditAmount,
                dataUsageConsent
              }
              adyenCommenceSessionAnalyticsCallback?.(commenceSessionParams)
              const commencedPayment = await commenceSessionCallback(commenceSessionParams)
              if (component.props.amount.value !== commencedPayment.get('adyenAmount').amount) {
                setError({
                  type: ERRORS.PARSE_ERROR,
                  detials: {
                    message: 'adyenAmount in payment and the actual amount differ',
                    details: {
                      paymentAdyenAmount: component.props.amount.value,
                      clientAdyenAmount: commencedPayment.get('adyenAmount').amount,
                      payment: commencedPayment.id
                    }
                  }
                })
                actions.reject()
              }
              actions.resolve(data)
            } catch (e) {
              captureException(e)

              setError({ type: ERRORS.PARSE_ERROR, details: e })
              actions.reject()
              setLoading(false)
            }
          },
          onActionHandled: async ({ componentType }) => {
            const selectedPaymentOptions =
              document.getElementsByClassName('selected_payment_option')
            if (selectedPaymentOptions.length > 0) {
              const element = selectedPaymentOptions[0]
              window.scrollTo({
                top: element.getBoundingClientRect().top + window.scrollY - 100,
                behavior: 'smooth'
              })
            }
            try {
              await Parse.Cloud.run('handleAdyenSessionAction', {
                paymentId: session.reference,
                action: componentType
              })
            } catch (error) {
              captureException(new Error(`Failed running handleAdyenSessionAction. ${error}`))
            }
          },

          onChange: (state) => {
            if (state.isValid) {
              setError(null)
            }
          }
        })

        if (checkout?.paymentMethodsResponse?.paymentMethods) {
          checkout.paymentMethodsResponse.paymentMethods.forEach((props) => {
            switch (props.type) {
              case 'saved_card':
              case 'card':
                new Card(checkout, { ...props, enableStoreDetails: Boolean(userId) })
                break
              case 'scheme':
                // The component will either not render or trigger an error if the type isn't changed here.
                new Card(checkout, { ...props, type: 'card' })
                break
              case 'paypal':
                new PayPal(checkout, {
                  ...props,
                  showPayButton: true,
                  // Optional configuration for PayPal payment buttons.
                  style: { layout: 'vertical', color: 'gold', disableMaxWidth: true }
                })
                break
              case 'swish':
                new Swish(checkout, props)
                break
              case 'klarna_paynow':
              case 'klarna_account':
              case 'klarna':
                new Klarna(checkout, props)
                break
              case 'ideal':
                // NOTE: `props.type` is already equal to 'ideal', but there's no harm in being explicit about it
                new Redirect(checkout, { ...props, type: 'ideal', issuer: '1160' })
                break
              case 'applepay':
                new ApplePay(checkout, { ...props, showPayButton: true })
                break
              case 'bcmc':
                new Bancontact(checkout, props)
                break
              case 'onlineBanking_PL':
                new OnlineBankingPL(checkout, props)
                break
              case 'mobilepay':
                new Redirect(checkout, props)
                break
            }
          })
          // isLoyaltyCheckout here disables stored card, potentially temporary fix
          // until we can make certain adyenRecurringDetailReference can be collected on
          // stored cards.

          if (
            checkout?.paymentMethodsResponse?.storedPaymentMethods?.length > 0 &&
            getRegion() !== 'NL' &&
            !isLoyaltyCheckout
          ) {
            const props = checkout?.paymentMethodsResponse?.storedPaymentMethods[0]
            new Card(checkout, { ...props, enableStoreDetails: Boolean(userId) })
          }
          if (redirectResult) {
            checkout.submitDetails({ details: { redirectResult: redirectResult } })
          }
          const allowedPaymentMethodComponents = ADYEN_PAYMENT_OPTIONS.map((option) => {
            const component = checkout?.components?.find(
              (component) => getDetailedType(component) === option
            )

            return component
          })
            .filter(Boolean)
            .filter((component) => {
              if (getDetailedType(component) === 'applepay') {
                return !isAppCheckout && isApplePayAvailable()
              }
              return true
            })
          // It's not optimal to mutate the checkout object, but since it's being widely used
          // this is an easy way of getting the components sortd everywhere
          checkout.components = allowedPaymentMethodComponents

          setAdyenCheckout(checkout)
        }
        setLoading(false)
      } catch (error) {
        captureException(error)

        setError({ type: ERRORS.ADYEN_ERROR, details: { message: error.message } })
        setLoading(false)
      }
    }

    getSession()
  }, [
    userId,
    pathname,
    search,
    creditAmount,
    navigate,
    getDataUsageConsent,
    createSessionCallback,
    isLoyaltyCheckout,
    commenceSessionCallback,
    totalPrice?.amount
  ])

  return {
    commencePayment,
    adyenCheckout,
    loading,
    paymentId,
    error
  }
}

export { useAdyenCheckout, useAdyenPaymentMethods, getDetailedType }
